import React, { useEffect, useState } from "react";
import { zodResolver } from "@hookform/resolvers/zod";
import { useForm, useWatch, type SubmitHandler } from "react-hook-form";
import { useTranslation } from "react-i18next";
import { toast } from "sonner";
import { useAddHouseSchema } from "@features/AddHouseModal/useAddHouseSchema";
import { Button } from "@components/Button/Button";
import { ExpandingContainer } from "@components/ExpandingContainer/ExpandingContainer";
import { HouseIcon } from "@components/icons/common";
import { FormAddressInput } from "@components/inputs/form/FormAddressInput/FormAddressInput";
import { FormHouseDetailsInput } from "@components/inputs/form/FormHouseDetailsInput/FormHouseDetailsInput";
import { FormInput } from "@components/inputs/form/FormInput/FormInput";
import { FormSelect } from "@components/inputs/form/FormSelect/FormSelect";
import { Modal } from "@components/Modal/Modal";
import { useApiErrorUtils } from "@hooks/useApiErrorUtils";
import { useFeatureConfig } from "@hooks/useFeatureFlags";
import { useHouseName } from "@hooks/useHouseName";
import { useModal } from "@hooks/useModal";
import { type IUseModalProps } from "@store/modal/modal";
import { useCustomerDetailsQuery } from "@services/customer/queries";
import { useCreateJoinBillServiceMutation } from "@services/jointBillServices/mutations";
import {
  type HeatingTypeId,
  type JointBillServiceTypeId,
} from "@services/jointBillServices/types";
import { useOverviewQuery } from "@services/overview/queries";
import { isNullOrUndefined } from "@utils/common";
import { parseDecimalNumberInputValue } from "@utils/units";
import { type IAddressFormData } from "@/types";

interface IFormData extends IAddressFormData {
  displayName?: string;
  sortOrder?: number;
  firstName: string;
  lastName: string;
  jointBillServiceTypeId?: JointBillServiceTypeId;
  area?: string;
  heatingTypeId?: HeatingTypeId;
}

interface IAddHouseModalProps extends IUseModalProps {
  onSuccess?: (jointBillServiceId: number) => void;
}

export const AddHouseModal: React.FC<IAddHouseModalProps> = (props) => {
  const { onSuccess, ...rest } = props;
  const modal = useModal(rest);
  const { t } = useTranslation();
  const { isHouseAddressOptional } = useFeatureConfig();

  const { data: customerData } = useCustomerDetailsQuery();
  const { data: overviewData } = useOverviewQuery();

  const [addressDirty, setAddressDirty] = useState(false);

  const schema = useAddHouseSchema(isHouseAddressOptional, addressDirty);

  const {
    handleSubmit,
    control,
    resetField,
    setFocus,
    setValue,
    setError,
    formState,
  } = useForm<IFormData>({
    defaultValues: {
      firstName: customerData?.firstName,
      lastName: customerData?.lastName,
      sortOrder: (overviewData?.jointBillServices.length ?? 0) + 1,
    },
    resolver: zodResolver(schema),
  });

  const { setApiErrors } = useApiErrorUtils(setError);

  const [municipalityId, settlementId, streetId, house, apartment] = useWatch({
    name: ["municipalityId", "settlementId", "streetId", "house", "apartment"],
    control,
  });

  useEffect(() => {
    const houseDirty = Object.keys(formState.dirtyFields).some((key) =>
      [
        "municipalityId",
        "settlementId",
        "streetId",
        "house",
        "apartment",
      ].includes(key),
    );
    if (houseDirty) {
      setAddressDirty(true);
    } else {
      setAddressDirty(false);
    }
  }, [formState.dirtyFields, formState.dirtyFields.municipalityId]);

  const houseName = useHouseName(
    municipalityId,
    settlementId,
    streetId,
    house,
    apartment,
  );

  const sortOrderOptions = [
    ...(overviewData?.jointBillServices.map((_, index) => ({
      value: index + 1,
      label: index + 1,
    })) ?? []),
    {
      value: (overviewData?.jointBillServices.length ?? 0) + 1,
      label: (overviewData?.jointBillServices.length ?? 0) + 1,
    },
  ];

  const {
    mutateAsync: createJointBillService,
    isPending: isCreateJointBillServicePending,
  } = useCreateJoinBillServiceMutation();

  const createHouse: SubmitHandler<IFormData> = async (data) => {
    const address =
      (isHouseAddressOptional && addressDirty) || !isHouseAddressOptional
        ? {
            municipalityId: data.municipalityId ?? 0,
            settlementId: data.settlementId ?? 0,
            streetId: data.streetId ?? 0,
            houseNumber: data.house,
            apartmentNumber: data.apartment,
          }
        : {};

    const response = await createJointBillService({
      firstName: data.firstName,
      lastName: data.lastName,

      ...address,
      ...(data.displayName ? { displayName: data.displayName } : {}),
      orderNumber: data.sortOrder,
      ...(data.jointBillServiceTypeId
        ? { jointBillServiceTypeId: data.jointBillServiceTypeId }
        : {}),
      ...(!isNullOrUndefined(data.area)
        ? { area: parseDecimalNumberInputValue(data.area) }
        : {}),
      ...(data.heatingTypeId ? { heatingTypeId: data.heatingTypeId } : {}),
    }).catch(setApiErrors);

    if (!isNullOrUndefined(response)) {
      modal.close();
      if (response) {
        onSuccess?.(response);
      }
      toast.success(t("addHouseModal.toast.success"));
    }
  };

  return (
    <Modal
      modal={modal}
      badgeProps={{ icon: HouseIcon }}
      actionButtons={
        <React.Fragment>
          <Button variant="secondary" onClick={modal.close} className="w-full">
            {t("addHouseModal.buttons.cancel")}
          </Button>
          <Button
            onClick={handleSubmit(createHouse)}
            className="w-full"
            isLoading={isCreateJointBillServicePending}
          >
            {t("addHouseModal.buttons.submit")}
          </Button>
        </React.Fragment>
      }
      title={t("addHouseModal.title")}
      subtitle={t("addHouseModal.subtitle")}
      className="md:max-w-xl"
    >
      <div className="flex flex-col gap-4">
        <div className="grid grid-cols-4 gap-4">
          <div className="col-span-3">
            <FormInput
              name="displayName"
              placeholder={houseName}
              control={control}
              label={t("addHouseModal.form.fields.displayName.label")}
            />
          </div>
          <FormSelect
            name="sortOrder"
            control={control}
            options={sortOrderOptions}
            label={t("addHouseModal.form.fields.sortOrder.label")}
          />
        </div>
        <div className="flex flex-col gap-4 sm:flex-row">
          <FormInput
            name="firstName"
            control={control}
            label={t("addHouseModal.form.fields.firstName.label")}
          />
          <FormInput
            name="lastName"
            control={control}
            label={t("addHouseModal.form.fields.lastName.label")}
          />
        </div>
        <ExpandingContainer
          isEnabled={isHouseAddressOptional}
          buttonLabel={t("addHouseModal.form.completeInformationButton")}
          className="pt-4"
        >
          <FormAddressInput
            municipalityFieldName="municipalityId"
            settlementFieldName="settlementId"
            streetFieldName="streetId"
            houseFieldName="house"
            apartmentFieldName="apartment"
            control={control}
            resetField={resetField}
            setFocus={setFocus}
            setValue={setValue}
          />
          <FormHouseDetailsInput
            jointBillServiceTypeIdFieldName="jointBillServiceTypeId"
            areaFieldName="area"
            heatingTypeIdFieldName="heatingTypeId"
            control={control}
            setValue={setValue}
          />
        </ExpandingContainer>
      </div>
    </Modal>
  );
};
